import { RadioGroup } from "@headlessui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { Pill } from "@/components/Interface/Pill/Pill";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { useHandleError } from "@/hooks/useHandleError";
import { DataContext } from "@/provider/DataContextProvider";
import { useAddOption, useListAvailableOptions } from "@/services/api";
import { getEntry, withPreservedSimAlias } from "@/utils/dataContextHelpers";
import {
  Duration,
  findDurationPeer,
  isFlatHomeTariff,
  type TransformedProductsById,
  transformProducts,
} from "@/utils/tariffUtils";

type Props = {
  // Kept interactive-blocked while a concurrent basket mutation (e.g. the
  // voice-option auto-strip in Checkout.tsx) may still be in flight, so the
  // two mutations' basket responses can't race and clobber one another.
  disabled?: boolean;
  onSwitchingChange?: (isSwitching: boolean) => void;
};

/**
 * Checkout billing-period switch
 * Hidden for Flat Home and precharged SIMs (duration is locked there) and for
 * purchase-intent flows (the customer already decided monthly vs. yearly).
 *
 * When swapping the option, preserve entry.onp — Number Porting may already
 * have cleared isONP (user chose "no"). Re-deriving isONP from mobileAbos
 * would flip it back on and make /contracts fail with "MNP Data missing".
 */
export const CheckoutBillingPeriodSwitch = ({
  disabled = false,
  onSwitchingChange,
}: Props) => {
  const { t } = useTranslation();
  const { dataContext, setDataContext } = useContext(DataContext);
  const handleError = useHandleError();
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    onSwitchingChange?.(isSwitching);
    // Only report our own switching state changing, not identity churn of the callback prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSwitching]);

  const entry = getEntry(dataContext);
  const basketId = dataContext.basket?.id;
  const optionId =
    dataContext.selectedOption?.id ?? entry?.options?.[0]?.product?.id;
  const isFlatHome = isFlatHomeTariff(optionId);
  const isPurchaseIntent =
    dataContext.wizardEntry?.source === "purchase-intent";

  const { data: availableOptions } = useListAvailableOptions(
    basketId || -1,
    0,
    undefined,
    {
      query: {
        enabled: !!basketId && !isFlatHome && !isPurchaseIntent,
        refetchOnWindowFocus: false,
      },
    },
  );

  const { processedProductsById } = useMemo(
    () =>
      availableOptions
        ? transformProducts(availableOptions)
        : { processedProductsById: {} as TransformedProductsById },
    [availableOptions],
  );

  const currentProduct =
    (optionId !== undefined
      ? processedProductsById[String(optionId)]
      : undefined) ?? dataContext.selectedOption;

  const isPrecharged = !!currentProduct?.precharged;
  const currentDuration =
    currentProduct?.duration ??
    (currentProduct?.minContractDur === 365 ? Duration.year : Duration.month);

  const monthlyPeer = findDurationPeer(
    currentProduct,
    processedProductsById,
    Duration.month,
  );
  const yearlyPeer = findDurationPeer(
    currentProduct,
    processedProductsById,
    Duration.year,
  );

  const { mutateAsync: addOption } = useAddOption();

  const canShow =
    !isFlatHome &&
    !isPrecharged &&
    !isPurchaseIntent &&
    !!currentProduct &&
    !!monthlyPeer &&
    !!yearlyPeer &&
    !!basketId &&
    !!entry?.uuid;

  if (!canShow) {
    return null;
  }

  const selectedDuration =
    currentDuration === Duration.year ? Duration.year : Duration.month;

  const handleSelect = async (duration: Duration) => {
    if (duration === selectedDuration || isSwitching || disabled) {
      return;
    }

    const peer = duration === Duration.year ? yearlyPeer : monthlyPeer;
    if (!peer?.id || !basketId || !entry?.uuid) {
      return;
    }

    setIsSwitching(true);

    try {
      const basket = await addOption({
        basketId,
        uuid: entry.uuid,
        params: {
          optionId: peer.id,
          quantity: 1,
          // Keep the customer's Number Porting choice (cleared to false on "no").
          isONP: !!entry.onp,
        },
      });

      setDataContext((prev) => ({
        ...prev,
        basket: withPreservedSimAlias(prev.basket, basket),
        selectedOption: peer,
      }));

      // The basket now carries new price conditions (a different product id)
      // — bring the customer back to the top of Checkout so they see them,
      // rather than leaving them scrolled down at the switch itself.
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      handleError(error);
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <CardModule
      dataTestid="checkout-billing-period-switch"
      variant="white"
      title={t("Onboarding.sections.checkout.billingPeriod.title")}
      subtitle={
        <Trans
          t={t}
          i18nKey="Onboarding.sections.checkout.billingPeriod.description"
          components={{ strong: <strong /> }}
        />
      }
    >
      <RadioGroup
        className="flex flex-col gap-2"
        aria-label={t("Onboarding.sections.checkout.billingPeriod.title")}
        value={selectedDuration}
        onChange={(duration: Duration) => void handleSelect(duration)}
      >
        <RadioButtonOption
          name="checkout-billing-period"
          value={Duration.month}
          label={t("Onboarding.sections.checkout.billingPeriod.monthly")}
          disabled={isSwitching || disabled}
        />
        <div className="flex items-center gap-2">
          <RadioButtonOption
            name="checkout-billing-period"
            value={Duration.year}
            label={t("Onboarding.sections.checkout.billingPeriod.yearly")}
            disabled={isSwitching || disabled}
          />
          <Pill
            className="bg-surface-accent-rose text-foreground-primary"
            size="sm"
            text={t(
              "Onboarding.sections.checkout.billingPeriod.freeMonthBadge",
            )}
          />
        </div>
      </RadioGroup>
    </CardModule>
  );
};
